import * as React from 'react';
import { useToast } from '@/context/ToastContext';
import {
  buildEchoShareLink,
  buildEchoOgShareLink,
  openSocialShareUrl,
  tryNativeShareWithImage,
} from '@/utils/share';
import { LinkShare } from './share/LinkShare';
import { SocialShare } from './share/SocialShare';
import { InstagramShareModal } from './share/InstagramShareModal';
import { FacebookShareModal } from './share/FacebookShareModal';
import { ShareCard } from '@/components/share/ShareCard';
import { useShareImage } from '@/hooks/useShareImage';

interface ShareSectionProps {
  echoName: string;
}

/** Renders link-copy and social-share controls for an echo profile page. */
export const ShareSection: React.FC<ShareSectionProps> = ({ echoName }) => {
  const { addToast } = useToast();
  const [igModalOpen, setIgModalOpen] = React.useState(false);
  const [igCopied, setIgCopied] = React.useState(false);
  const [fbModalOpen, setFbModalOpen] = React.useState(false);
  const [fbCopied, setFbCopied] = React.useState(false);
  const [shareImageBlob, setShareImageBlob] = React.useState<Blob | null>(null);
  const [shareCardMounted, setShareCardMounted] = React.useState(false);
  const shareCardRef = React.useRef<HTMLDivElement>(null);
  const captureShareImage = useShareImage(shareCardRef);

  const shareLink = buildEchoShareLink(echoName);
  const ogShareLink = buildEchoOgShareLink(echoName);
  const shareText = `Meet ${echoName} on D.ECHO \u2014 an AI personality you can talk to!\n${shareLink}`;

  const mountAndCapture = React.useCallback(async (): Promise<Blob | null> => {
    setShareCardMounted(true);
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });
    return captureShareImage();
  }, [captureShareImage]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      addToast({ title: 'Copied!', subtitle: 'Link copied to clipboard.', type: 'success' });
    } catch {
      addToast({ title: 'Error', subtitle: 'Failed to copy link.', type: 'error' });
    }
  };

  const handleModalCopy = async (setCopied: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
    } catch {
      addToast({ title: 'Error', subtitle: 'Failed to copy link.', type: 'error' });
    }
  };

  const handleNativeShare = async () => {
    const imageBlob = await mountAndCapture();
    try {
      await tryNativeShareWithImage(shareText, shareLink, imageBlob, `${echoName} on D.ECHO`);
    } catch {
      /* user cancelled or unsupported */
    }
    setShareCardMounted(false);
  };

  const handleSocialShare = async (platform: string) => {
    if (platform === 'instagram' || platform === 'facebook') {
      const imageBlob = await mountAndCapture();
      setShareImageBlob(imageBlob);
      setShareCardMounted(false);
      if (platform === 'instagram') {
        setIgCopied(false);
        setIgModalOpen(true);
      } else {
        setFbCopied(false);
        setFbModalOpen(true);
      }
      return;
    }

    const encoded = encodeURIComponent(shareText);
    const linkForPlatform = platform === 'twitter' ? ogShareLink : shareLink;
    await openSocialShareUrl(platform, encoded, linkForPlatform, shareText);
  };

  return (
    <>
      <div className='flex w-full flex-col gap-12 lg:flex-row lg:gap-20'>
        <div className='flex-1'>
          <LinkShare link={shareLink} onCopy={handleCopy} />
        </div>
        <div className='flex-1'>
          <SocialShare
            onShare={handleSocialShare}
            onNativeShare={handleNativeShare}
            link={shareText}
            onCopy={handleCopy}
          />
        </div>
      </div>

      <InstagramShareModal
        isOpen={igModalOpen}
        onClose={() => {
          setIgModalOpen(false);
          setIgCopied(false);
        }}
        shareText={shareText}
        copied={igCopied}
        onCopy={() => handleModalCopy(setIgCopied)}
        imageBlob={shareImageBlob}
      />

      <FacebookShareModal
        isOpen={fbModalOpen}
        onClose={() => {
          setFbModalOpen(false);
          setFbCopied(false);
        }}
        shareText={shareText}
        copied={fbCopied}
        onCopy={() => handleModalCopy(setFbCopied)}
        shareUrl={ogShareLink}
        imageBlob={shareImageBlob}
      />

      {shareCardMounted && (
        <ShareCard
          ref={shareCardRef}
          text={`Meet ${echoName} on D.ECHO \u2014 an AI personality you can talk to!`}
          echoName={echoName}
        />
      )}
    </>
  );
};
